// API origin prefix (empty in dev where the SPA proxies same-origin). Exported
// so non-JSON fetches (e.g. the CSV export blob download, or a browser
// navigation to a streaming PDF download endpoint) can target the same base
// without routing through the JSON `api()` helper.
export const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

/**
 * Error thrown by `api()` on non-2xx responses. Carries the HTTP status so
 * callers can branch on specific codes (e.g. 409 retry for patient-create races).
 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(path: string, status: number, body: unknown) {
    super(`api ${path} ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  // Only set Content-Type when there's a body. Fastify's default JSON parser
  // rejects empty bodies with FST_ERR_CTP_EMPTY_JSON_BODY when the header is
  // set — so no-body POSTs (like /consults/:id/submit) must not advertise it.
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    // Never serve API reads from the browser HTTP cache. React Query owns
    // freshness in memory; a cached fetch response would defeat post-mutation
    // refetches (e.g. the specialist board not updating after accept/decline)
    // and risk persisting PHI to the on-disk cache. Mirrors the server's
    // Cache-Control: no-store — defense in depth.
    cache: "no-store",
    headers: {
      ...(init?.body != null ? { "Content-Type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    // Try to parse a JSON error body for ApiError to surface, but tolerate
    // empty / non-JSON bodies (e.g. 204).
    let body: unknown = null;
    try {
      const text = await res.text();
      body = text ? JSON.parse(text) : null;
    } catch {
      body = null;
    }
    throw new ApiError(path, res.status, body);
  }
  // Tolerate no-content responses (e.g. 204 from logout) — calling res.json()
  // on an empty body throws. Callers expecting no content type this as void.
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}
